import { AnalysisResult } from "@/app/actions/analyze-idea";
import { db } from "@/lib/firebase";
import { collection, deleteDoc, doc, getDoc, getDocs, orderBy, query, setDoc } from "firebase/firestore";

export interface HistoryItem {
    prompt: string;
    result: AnalysisResult;
    timestamp: number;
}

export interface Idea {
    id: string;
    description: string;
    createdAt: number;
    history: HistoryItem[];
    lastUpdated: number;
}

const GUEST_STORAGE_KEY = 'guest_ideas';
const GUEST_UUID_KEY = 'guest_uuid';

export const getOrCreateGuestUuid = (): string => {
    if (typeof window === 'undefined') return '';
    let uuid = localStorage.getItem(GUEST_UUID_KEY);
    if (!uuid) {
        uuid = crypto.randomUUID();
        localStorage.setItem(GUEST_UUID_KEY, uuid);
    }
    return uuid;
};

export const IdeaService = {
    // Save or Update an Idea
    async saveIdea(userId: string | null, idea: Idea): Promise<void> {
        const id = userId || (typeof window !== 'undefined' ? `guest_${getOrCreateGuestUuid()}` : null);
        if (id) {
            // Firestore
            const ideaRef = doc(db, "users", id, "ideas", idea.id);
            await setDoc(ideaRef, idea);
        }

        if (!userId) {
            // LocalStorage fallback/cache
            const ideas = this.getGuestIdeas();
            const index = ideas.findIndex(i => i.id === idea.id);
            if (index >= 0) {
                ideas[index] = idea;
            } else {
                ideas.push(idea);
            }
            localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(ideas));
        }
    },

    // Get all ideas for a user
    async getIdeas(userId: string | null): Promise<Idea[]> {
        const id = userId || (typeof window !== 'undefined' ? `guest_${getOrCreateGuestUuid()}` : null);
        if (id) {
            try {
                const q = query(collection(db, "users", id, "ideas"), orderBy("lastUpdated", "desc"));
                const snapshot = await getDocs(q);
                return snapshot.docs.map(doc => doc.data() as Idea);
            } catch (e) {
                console.error("Error fetching ideas from Firestore", e);
                if (!userId) {
                    return this.getGuestIdeas().sort((a, b) => b.lastUpdated - a.lastUpdated);
                }
                return [];
            }
        } else {
            return this.getGuestIdeas().sort((a, b) => b.lastUpdated - a.lastUpdated);
        }
    },

    // Get a specific idea
    async getIdea(userId: string | null, ideaId: string): Promise<Idea | null> {
        const id = userId || (typeof window !== 'undefined' ? `guest_${getOrCreateGuestUuid()}` : null);
        if (id) {
            try {
                const docRef = doc(db, "users", id, "ideas", ideaId);
                const snapshot = await getDoc(docRef);
                if (snapshot.exists()) {
                    return snapshot.data() as Idea;
                }
            } catch (e) {
                console.error("Error fetching idea from Firestore", e);
            }
        }

        if (!userId) {
            const ideas = this.getGuestIdeas();
            return ideas.find(i => i.id === ideaId) || null;
        }
        return null;
    },

    // Delete an idea
    async deleteIdea(userId: string | null, ideaId: string): Promise<void> {
        const id = userId || (typeof window !== 'undefined' ? `guest_${getOrCreateGuestUuid()}` : null);
        if (id) {
            try {
                await deleteDoc(doc(db, "users", id, "ideas", ideaId));
            } catch (e) {
                console.error("Error deleting idea from Firestore", e);
            }
        }
        if (!userId) {
            const ideas = this.getGuestIdeas();
            const newIdeas = ideas.filter(i => i.id !== ideaId);
            localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(newIdeas));
        }
    },

    // Migrate guest ideas to user
    async migrateGuestIdeas(userId: string): Promise<void> {
        if (typeof window === 'undefined') return;
        const guestIdeas = this.getGuestIdeas();
        if (guestIdeas.length === 0) return;

        try {
            // Save all to Firestore under user account
            const promises = guestIdeas.map(idea => this.saveIdea(userId, idea));
            await Promise.all(promises);

            // Delete old guest ideas from Firestore to clean up
            const guestId = `guest_${getOrCreateGuestUuid()}`;
            const deletePromises = guestIdeas.map(idea => deleteDoc(doc(db, "users", guestId, "ideas", idea.id)));
            await Promise.all(deletePromises);

            // Clear guest storage and guest UUID
            localStorage.removeItem(GUEST_STORAGE_KEY);
            localStorage.removeItem(GUEST_UUID_KEY);
        } catch (e) {
            console.error("Migration failed", e);
        }
    },

    // Helper for guest ideas
    getGuestIdeas(): Idea[] {
        if (typeof window === 'undefined') return [];
        const stored = localStorage.getItem(GUEST_STORAGE_KEY);
        if (!stored) return [];
        try {
            return JSON.parse(stored);
        } catch {
            return [];
        }
    }
};
